import { onBeforeUnmount, onMounted, ref, type ComputedRef } from 'vue'
import { mediaLog, type ResolvedMedia } from '../flowchart/runtime/media'
import type { FlowRunnerProps } from '../flowchart/runtime/useFlowchartRunner'
import type { FlowSessionState } from '../flowchart/runtime/sessionState'

export const NOW_PLAYING_SEEK_SECONDS = 10

const parseMessage = (event: MessageEvent): Record<string, unknown> | null => {
  let data: unknown = event.data
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data)
    } catch {
      return null
    }
  }
  return typeof data === 'object' && data !== null ? (data as Record<string, unknown>) : null
}

/**
 * Playback of the media a show-media node presents. Non-skippable audio/video hides
 * native controls but may be paused/resumed (button or Space) and moved by fixed
 * steps — never scrubbed. When it ends, the host is asked to show its Continue.
 */
export function useNowPlayingMedia(
  props: Pick<FlowRunnerProps, 'onMediaPause'>,
  state: FlowSessionState,
  resolvedMedia: ComputedRef<ResolvedMedia | null>,
) {
  const mediaEl = ref<HTMLMediaElement | null>(null)
  const youtubeFrame = ref<HTMLIFrameElement | null>(null)
  const vimeoFrame = ref<HTMLIFrameElement | null>(null)
  const isPlaying = ref(true)
  // Tracked from YouTube infoDelivery / Vimeo timeupdate so seeking can be relative.
  const youtubeTime = ref(0)
  const vimeoTime = ref(0)
  const continuationAnnounced = ref(false)

  /** Called when a blocking media node is shown. */
  const reset = () => {
    continuationAnnounced.value = false
    youtubeTime.value = 0
    vimeoTime.value = 0
  }

  /** Lets the host show its Continue button for the media on screen (once). */
  const announceContinuation = () => {
    const media = state.showingMedia.value
    const resume = state.showMediaResume.value
    if (!media || !resume || continuationAnnounced.value) {
      mediaLog('playback-ended continuation ignored', {
        hasMedia: Boolean(media),
        hasResume: Boolean(resume),
        alreadyAnnounced: continuationAnnounced.value,
      })
      return
    }
    continuationAnnounced.value = true
    mediaLog('playback ended → requesting Continue', {
      mediaType: media.mediaType,
      mediaId: media.mediaId,
      displayMode: media.displayMode,
      hasHostCallback: Boolean(props.onMediaPause),
    })
    props.onMediaPause?.(
      {
        displayMode: media.displayMode,
        mediaType: media.mediaType,
        mediaName: media.mediaName,
        mediaId: media.mediaId,
      },
      resume,
    )
  }

  const logNativeEvent = (event: Event) => {
    const media = event.currentTarget as HTMLMediaElement
    mediaLog(`native ${media.tagName.toLowerCase()} ${event.type}`, {
      currentTime: Number(media.currentTime.toFixed(3)),
      duration: Number.isFinite(media.duration) ? Number(media.duration.toFixed(3)) : null,
      paused: media.paused,
      playbackRate: media.playbackRate,
    })
  }

  const onNativeEnded = (event: Event) => {
    logNativeEvent(event)
    announceContinuation()
  }
  const onNativePlay = (event: Event) => {
    logNativeEvent(event)
    isPlaying.value = true
  }
  const onNativePause = (event: Event) => {
    logNativeEvent(event)
    isPlaying.value = false
  }

  const toggle = () => {
    const el = mediaEl.value
    if (el) {
      if (el.paused) void el.play()
      else el.pause()
      return
    }
    if (youtubeFrame.value) {
      const func = isPlaying.value ? 'pauseVideo' : 'playVideo'
      isPlaying.value = !isPlaying.value
      youtubeFrame.value.contentWindow?.postMessage(
        JSON.stringify({ event: 'command', func, args: [] }),
        '*',
      )
      return
    }
    if (!vimeoFrame.value) return
    const method = isPlaying.value ? 'pause' : 'play'
    isPlaying.value = !isPlaying.value
    vimeoFrame.value.contentWindow?.postMessage(JSON.stringify({ method }), '*')
  }

  /** Rewind/forward by a fixed step, so unwatched parts cannot be skipped. */
  const seek = (deltaSeconds: number) => {
    const el = mediaEl.value
    if (el) {
      const duration = Number.isFinite(el.duration) && el.duration > 0 ? el.duration : Infinity
      el.currentTime = Math.min(Math.max(0, el.currentTime + deltaSeconds), duration)
      return
    }
    if (youtubeFrame.value) {
      const target = Math.max(0, youtubeTime.value + deltaSeconds)
      youtubeTime.value = target
      youtubeFrame.value.contentWindow?.postMessage(
        JSON.stringify({ event: 'command', func: 'seekTo', args: [target, true] }),
        '*',
      )
      return
    }
    if (!vimeoFrame.value) return
    const target = Math.max(0, vimeoTime.value + deltaSeconds)
    vimeoTime.value = target
    vimeoFrame.value.contentWindow?.postMessage(
      JSON.stringify({ method: 'setCurrentTime', value: target }),
      '*',
    )
  }

  const initYoutube = (event: Event) => {
    const frame = event.currentTarget as HTMLIFrameElement
    frame.contentWindow?.postMessage(JSON.stringify({ event: 'listening', id: 1 }), '*')
    frame.contentWindow?.postMessage(
      JSON.stringify({ event: 'command', func: 'addEventListener', args: ['onStateChange'] }),
      '*',
    )
  }

  const initVimeo = (event: Event) => {
    const frame = event.currentTarget as HTMLIFrameElement
    for (const value of ['play', 'pause', 'ended', 'timeupdate']) {
      frame.contentWindow?.postMessage(JSON.stringify({ method: 'addEventListener', value }), '*')
    }
  }

  const onKeydown = (event: KeyboardEvent) => {
    if (event.code !== 'Space' && event.key !== ' ') return
    const media = resolvedMedia.value
    if (!media || (media.mediaType !== 'audio' && media.mediaType !== 'video') || media.skippable)
      return
    const target = event.target as HTMLElement | null
    if (target?.closest('input, textarea, select, [contenteditable="true"]')) return
    event.preventDefault()
    toggle()
  }

  // Admin bar "Skip Media" test control: show the host's Continue without waiting.
  const onAdminSkip = () => {
    if (!state.showingMedia.value) return
    announceContinuation()
  }

  const onYoutubeMessage = (event: MessageEvent) => {
    if (!event.origin.endsWith('youtube.com') && !event.origin.endsWith('youtube-nocookie.com'))
      return
    const data = parseMessage(event)
    if (!data) return
    if (data.event === 'infoDelivery') {
      const currentTime = (data.info as { currentTime?: unknown } | undefined)?.currentTime
      if (typeof currentTime === 'number') youtubeTime.value = currentTime
      return
    }
    if (data.event === 'onStateChange') {
      const playerState = data.info
      mediaLog('YouTube state changed', { state: playerState })
      if (playerState === 1) isPlaying.value = true
      else if (playerState === 2) isPlaying.value = false
      if (playerState === 0) announceContinuation()
    }
  }

  const onVimeoMessage = (event: MessageEvent) => {
    if (!event.origin.endsWith('vimeo.com')) return
    const data = parseMessage(event)
    if (!data) return
    if (data.event === 'timeupdate') {
      const seconds = (data.data as { seconds?: unknown } | undefined)?.seconds
      if (typeof seconds === 'number') vimeoTime.value = seconds
      return
    }
    mediaLog('Vimeo event', { evt: data.event })
    if (data.event === 'play') isPlaying.value = true
    else if (data.event === 'pause') isPlaying.value = false
    else if (data.event === 'ended') announceContinuation()
  }

  onMounted(() => window.addEventListener('keydown', onKeydown))
  onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown))
  onMounted(() => window.addEventListener('perlingo:admin-skip-media', onAdminSkip))
  onBeforeUnmount(() => window.removeEventListener('perlingo:admin-skip-media', onAdminSkip))
  onMounted(() => window.addEventListener('message', onYoutubeMessage))
  onBeforeUnmount(() => window.removeEventListener('message', onYoutubeMessage))
  onMounted(() => window.addEventListener('message', onVimeoMessage))
  onBeforeUnmount(() => window.removeEventListener('message', onVimeoMessage))

  /** Template ref callbacks; the element on screen wins, unmounting clears it. */
  const bindMediaEl = (el: unknown) => {
    mediaEl.value = (el as HTMLMediaElement | null) ?? null
  }
  const bindYoutubeFrame = (el: unknown) => {
    youtubeFrame.value = (el as HTMLIFrameElement | null) ?? null
  }
  const bindVimeoFrame = (el: unknown) => {
    vimeoFrame.value = (el as HTMLIFrameElement | null) ?? null
  }

  return {
    isPlaying,
    reset,
    toggle,
    seek,
    initYoutube,
    initVimeo,
    logNativeEvent,
    onNativeEnded,
    onNativePlay,
    onNativePause,
    bindMediaEl,
    bindYoutubeFrame,
    bindVimeoFrame,
  }
}

export type NowPlayingMedia = ReturnType<typeof useNowPlayingMedia>
